/* Visual bench for the LiveAdvice card variants.
 *
 * Renders every per-type card with seed data shaped to match the
 * mockup states (agentic_copilot_mockup_v4.html). Lets us headless-
 * screenshot a single page and diff against the mockup screenshots
 * without needing a live call.
 *
 * Routed at /dev/advice-bench. No live data, no fetches — purely
 * static seed payloads piped into the same components production
 * uses, so any visual gap from the mockup shows up here too.
 */

import type { ReactNode } from "react";
import { AppShell } from "./AppShell";
import {
  CallOutcomeCard,
  CoachingCard,
  CompetitorBattlecardCard,
  IntentRootCauseCard,
  NudgeBanner,
  OfferPresentationCard,
  SuggestedApproachCard,
  type Card,
} from "./agent-desk/LiveAdvice";

// Stable timestamps so screenshots diff cleanly run-to-run.
const TS = "2026-05-05T12:00:00.000Z";

const noop = () => undefined;

// ─── Seed cards ────────────────────────────────────────────────────────
// Each card is shaped to mirror its mockup state's hero content. Numbers
// + names match the mockup's reference scenario (Ryan M. / AT&T).

const suggestedApproach: Card = {
  id: 1,
  ts: TS,
  type: "suggested_approach",
  title: "Lead with empathy — flag the bill issue first",
  body: "High-CLV, no prior saves, promo expiry on lines 2 & 3 already detected. Acknowledge proactively before account details.",
  confidence: 0.9,
  evidence: null,
  payload: {
    leads: [
      "Customer's bill went up $28/mo in January — promo ended on 2 of 3 lines",
      "Apologize that this wasn't flagged earlier — sets the right tone",
      "Then move to: 'let's get to the bottom of this'",
    ],
    why: "high-CLV customer · bill shock pre-classified · no prior save attempts",
  },
};

const intentRootCause: Card = {
  id: 2,
  ts: TS,
  type: "intent_root_cause",
  title: "Bill shock confirmed — promo expiry root cause",
  body: "Customer confirms the increase wasn't flagged. Sentiment trending negative — handle empathy before pivoting.",
  confidence: 0.95,
  evidence: null,
  payload: {
    rows: [
      { label: "PRIMARY INTENT", value: "Bill shock", status: "CONFIRMED" },
      { label: "ROOT CAUSE", value: "Promo expiry · Lines 2 & 3", status: "" },
      { label: "BILL IMPACT", value: "+$28/mo · Jan onward", status: "" },
      { label: "SENTIMENT", value: "Frustrated", status: "RISING" },
    ],
    why: 'customer said "nobody told me" · bill increase matches detected promo expiry',
  },
};

const competitorBattlecard: Card = {
  id: 3,
  ts: TS,
  type: "competitor_battlecard",
  title: "AT&T pricing competitive threat",
  body: "Customer mentioned AT&T Unlimited Starter by name. Lead with intro-rate expiry — biggest hook.",
  confidence: 0.97,
  evidence: null,
  payload: {
    competitor_name: "AT&T Unlimited Starter — 3 lines @ $120/mo",
    comparison: [
      { feature: "Price (3 lines)", theirs: "$120 → $155 after 3mo", ours: "$165 locked 12mo" },
      { feature: "Hotspot", theirs: "Not included", ours: "Included" },
      { feature: "Premium network", theirs: "Not included", ours: "Included" },
      { feature: "Network rank in zip", theirs: "—", ours: "#1 in Ryan's area" },
    ],
    hooks: [
      "Hook: $120 is intro only — jumps to $155/mo after 3 months",
      "Anchor: Verizon network rated #1 in Ryan's zip code",
      'Pivot: "Let me show you what I can do for you personally"',
    ],
    why: '"AT&T" + "$40 a line" detected in transcript at 01:14 · matched to competitor catalog',
  },
};

const offerPresentation: Card = {
  id: 4,
  ts: TS,
  type: "offer_presentation",
  title: "myPlan + Loyalty Discount",
  body: "CLV-ranked top offer. Locked 12 months — direct counter to AT&T's intro-rate jump.",
  confidence: 0.92,
  evidence: null,
  payload: {
    offer_code: "SAVE-LOYALTY-T1",
    current_monthly_cents: 21000,
    new_monthly_cents: 16500,
    lock_months: 12,
    features: ["Hotspot", "Premium network"],
    leads: [
      "Lead: \"There's an offer available specifically for you given how long you've been with us\"",
      "Anchor: 12-month lock — unlike AT&T's intro rate",
    ],
    why: "high CLV · no prior saves · competitor-driven · 7-yr tenure unlocks loyalty rung",
  },
};

const callOutcome: Card = {
  id: 5,
  ts: TS,
  type: "call_outcome",
  title: "myPlan migration + $20 loyalty discount applied",
  body: "Confirmation sent to Ryan's phone · 12-month lock-in active · CRM auto-logged",
  confidence: 1,
  evidence: null,
  payload: {
    outcome: "SAVED",
    summary: "Confirmation sent to Ryan's phone · 12-month lock-in active · CRM auto-logged",
    rows: [
      { label: "Outcome", value: "SAVED" },
      { label: "Offer used", value: "Tier 1 · myPlan + Loyalty" },
      { label: "Root cause", value: "Competitor (AT&T)" },
      { label: "CLV retained", value: "$165/mo · 12mo" },
      { label: "Call duration", value: "8m 14s" },
      { label: "Outcome tag (auto)", value: "SAVE-COMP-T1" },
    ],
    outcome_tag: "SAVE-COMP-T1",
  },
};

const coaching: Card = {
  id: 6,
  ts: TS,
  type: "coaching",
  title: "Strong empathy, late competitor pivot",
  body: "Personalized for you, based on this call. Not shared with anyone else.",
  confidence: 0.9,
  evidence: null,
  payload: {
    overall_score: 87,
    summary: "Strong empathy, late competitor pivot",
    scores: [
      { label: "Empathy", value: 92, tier: "good" },
      { label: "Probing", value: 85, tier: "good" },
      { label: "Close technique", value: 90, tier: "good" },
      { label: "Script adherence", value: 88, tier: "good" },
      { label: "Proactivity", value: 72, tier: "mid" },
      { label: "Pace & talk ratio", value: 84, tier: "good" },
    ],
    what_went_well: {
      timestamp_secs: 33,
      text: "You acknowledged the bill issue and apologized before diving into account details — sentiment shifted positive immediately after.",
      quote: '"I apologize we didn\'t flag that for you earlier."',
    },
    one_thing_to_try_next: {
      timestamp_secs: 158,
      text: "You introduced the loyalty offer before flagging AT&T's intro-rate expiry. Top performers lead with the price-lock contrast — it tends to land better when customers are still anchored on competitor pricing.",
      quote: 'Try: "Worth knowing — that AT&T price jumps to $155 after 3 months. Here\'s what I can lock in for you for 12 months..."',
    },
    trend: {
      save_rate_this_week: "↑ 58%",
      save_rate_last_week: "51%",
      competitor_saves_today: "3 / 4",
      summary: "Strong week. Your competitor-driven save rate is up — keep leaning on the price-lock framing.",
    },
  },
};

const alertNudge: Card = {
  id: 7,
  ts: TS,
  type: "nudge",
  title: "Frustration detected",
  body: "acknowledge before responding",
  confidence: 0.85,
  evidence: '"You\'re right, we should have flagged that for you in advance."',
  payload: {
    severity: "alert",
    message: 'Suggested phrasing: "You\'re right, we should have flagged that for you in advance."',
    evidence: "we should have flagged that for you in advance",
  },
};

const sentimentNudge: Card = {
  id: 8,
  ts: TS,
  type: "nudge",
  title: "Probe deeper before pivoting",
  body: "reason still surface-level",
  confidence: 0.8,
  evidence: '"Is the bill increase the main reason, or has anything else been frustrating you?"',
  payload: {
    severity: "sentiment",
    message: 'Try: "Is the bill increase the main reason, or has anything else been frustrating you?"',
    evidence: "the bill increase the main reason",
  },
};

// ─── Layout ────────────────────────────────────────────────────────────
type Section = {
  id: string;
  title: string;
  mockupRef: string;
  render: () => ReactNode;
};

const SECTIONS: Section[] = [
  {
    id: "suggested-approach",
    title: "Suggested approach",
    mockupRef: "Mockup state 1 — Call start",
    render: () => <SuggestedApproachCard card={suggestedApproach} onDismiss={noop} />,
  },
  {
    id: "intent-root-cause",
    title: "Intent + root cause",
    mockupRef: "Mockup state 2 — Root cause confirmed",
    render: () => <IntentRootCauseCard card={intentRootCause} onDismiss={noop} />,
  },
  {
    id: "nudge-alert",
    title: "Alert nudge",
    mockupRef: "Mockup state 2 — slim banners above hero",
    render: () => (
      <NudgeBanner card={alertNudge} severity="alert" onDismiss={noop} />
    ),
  },
  {
    id: "nudge-sentiment",
    title: "Sentiment nudge",
    mockupRef: "Mockup state 2 — slim banners above hero",
    render: () => (
      <NudgeBanner card={sentimentNudge} severity="sentiment" onDismiss={noop} />
    ),
  },
  {
    id: "competitor-battlecard",
    title: "Competitor battlecard",
    mockupRef: "Mockup state 3 — Competitor battlecard",
    render: () => <CompetitorBattlecardCard card={competitorBattlecard} onDismiss={noop} />,
  },
  {
    id: "offer-presentation",
    title: "Offer presentation",
    mockupRef: "Mockup state 4 — Offer presentation",
    render: () => <OfferPresentationCard card={offerPresentation} onDismiss={noop} />,
  },
  {
    id: "call-outcome",
    title: "Call outcome",
    mockupRef: "Mockup state 5 — Close + post-call",
    render: () => <CallOutcomeCard card={callOutcome} onDismiss={noop} />,
  },
  {
    id: "coaching",
    title: "Coaching",
    mockupRef: "Mockup state 5 — Coach card below outcome",
    render: () => <CoachingCard card={coaching} onDismiss={noop} />,
  },
];

export function AdviceBench() {
  // ?only=<id> filters to a single section so headless screenshot
  // captures one variant cleanly. No `only` param = render everything.
  const params = new URLSearchParams(window.location.search);
  const only = params.get("only");
  const sections = only ? SECTIONS.filter((s) => s.id === only) : SECTIONS;

  return (
    <AppShell>
      <div className="advice-bench">
        {!only && (
          <header className="advice-bench-header">
            <h1>LiveAdvice variant bench</h1>
            <p>
              Static render of every advisor card variant with seed data
              shaped to match the mockup states. Use for visual diffing
              against <code>agentic_copilot_mockup_v4.html</code>. Add
              <code>?only=&lt;section-id&gt;</code> to focus one variant.
            </p>
          </header>
        )}
        {sections.map((s) => (
          <section
            key={s.id}
            id={`bench-${s.id}`}
            className="advice-bench-section"
          >
            <div className="advice-bench-section-head">
              <h2>{s.title}</h2>
              <span className="advice-bench-section-ref">{s.mockupRef}</span>
            </div>
            <div className="advice-bench-section-stage">
              <div className="agent-desk-live-advice">{s.render()}</div>
            </div>
          </section>
        ))}
      </div>
    </AppShell>
  );
}
